import React, { ReactNode, useId, useState } from 'react';


interface PortfolioAnalysisTabsProps {
  canViewEffectiveMargin: boolean;
  filteredPositionCount: number;
  totalPositionCount: number;
  positions: ReactNode;
  effective: (showPositions: () => void) => ReactNode;
}

type AnalysisTab = 'positions' | 'effective';

const PortfolioAnalysisTabs: React.FC<PortfolioAnalysisTabsProps> = ({
  canViewEffectiveMargin,
  filteredPositionCount,
  totalPositionCount,
  positions,
  effective,
}) => {
  const [activeTab, setActiveTab] = useState<AnalysisTab>('positions');
  const visibleTab = canViewEffectiveMargin ? activeTab : 'positions';
  const id = useId();
  const positionsTabId = `${id}-positions-tab`;
  const positionsPanelId = `${id}-positions-panel`;
  const effectiveTabId = `${id}-effective-tab`;
  const effectivePanelId = `${id}-effective-panel`;
  const showPositions = () => setActiveTab('positions');

  return (
    <section aria-labelledby={`${id}-heading`} className="mt-[22px]">
      <div className="mb-3 flex items-end justify-between gap-4">
        <div>
          <h2 id={`${id}-heading`} className="m-0 font-serif text-[22px] font-normal tracking-[-0.005em]">Portfolio Analysis</h2>
          {canViewEffectiveMargin && <p className="mb-0 mt-1 text-[11px] text-[#666]">Choose the raw position rows or their Effective Margin interpretation.</p>}
        </div>
      </div>

      <div role="tablist" aria-label="Portfolio analysis views" className="flex gap-6 border border-black/10 border-b-0 bg-white px-4 pt-1">
        <button
          id={positionsTabId}
          type="button"
          role="tab"
          aria-selected={visibleTab === 'positions'}
          aria-controls={positionsPanelId}
          onClick={showPositions}
          className={`border-0 border-b-2 bg-transparent px-1 py-3 text-[12px] font-semibold ${visibleTab === 'positions' ? 'border-[#E50025] text-[#111]' : 'border-transparent text-[#666] hover:text-[#111]'}`}
        >
          Portfolio Positions <span className="ml-1.5 rounded-full bg-black/[0.06] px-2 py-0.5 text-[10px]">{filteredPositionCount} of {totalPositionCount}</span>
        </button>
        {canViewEffectiveMargin && <button
          id={effectiveTabId}
          type="button"
          role="tab"
          aria-selected={activeTab === 'effective'}
          aria-controls={effectivePanelId}
          onClick={() => setActiveTab('effective')}
          className={`border-0 border-b-2 bg-transparent px-1 py-3 text-[12px] font-semibold ${activeTab === 'effective' ? 'border-[#E50025] text-[#111]' : 'border-transparent text-[#666] hover:text-[#111]'}`}
        >
          Effective Margin Analysis <span className="ml-1.5 border border-[#E50025]/25 bg-[#E50025]/[0.07] px-1.5 py-0.5 text-[9px] font-bold text-[#B00020]">NEW</span>
        </button>}
      </div>

      {visibleTab === 'positions' ? (
        <div id={positionsPanelId} role="tabpanel" aria-labelledby={positionsTabId}>{positions}</div>
      ) : (
        <div id={effectivePanelId} role="tabpanel" aria-labelledby={effectiveTabId}>{effective(showPositions)}</div>
      )}
    </section>
  );
};

export default PortfolioAnalysisTabs;
